/**
 * "Brand color panel" algorithm for the SRP card + job detail page redesign (V2).
 * Takes a company's raw extracted brand hex (DisplayJob.brandColour, computed once server-side
 * by utils/image/compute-brand-colour.ts) and turns it into a confident, legible panel fill +
 * on-color text pair. Deliberately separate from components/Hit/utils/brandColorUtils.ts, which
 * the existing (V1) card/detail design still uses.
 *
 * Rules (must match the design handoff exactly):
 * 1. If the color's saturation is under 12% (effectively black/white/gray, e.g. a monochrome
 *    logo mark), don't force a color in — fall back to the neutral ink panel.
 * 2. Otherwise clamp lightness to 32-58% and apply a 55% saturation floor, so pale/pastel brand
 *    colors get deepened into a confident block instead of staying washed out.
 * 3. Pick panel text color (white vs. dark ink) by measured WCAG contrast ratio against the
 *    adjusted panel color — never assumed from the hue alone.
 * 4. Also derive `accentOnWhite`: the same hue, darkened only as far as needed to clear a 4.5:1
 *    contrast ratio against a WHITE background — for call sites (e.g. the brand-colored text on
 *    the white "Apply" pill) that paint the brand hue on white rather than on the panel itself.
 *    A bright/pale brand color (e.g. lime green) can pass step 3's panel-relative contrast check
 *    while still being nearly illegible as text on plain white; `on` and `accentOnWhite` answer
 *    two different questions and must not be conflated.
 */
import { jcThemeHexNextDark, jcThemeHexNextLight } from '../../constants/jcThemeHex';

export const NEUTRAL_PANEL_FALLBACK_HEX = '#17160F';
const DARK_INK_HEX = '#17160F';
const WHITE_HEX = '#FFFFFF';
const MIN_SATURATION_FOR_BRAND_HUE = 12;
const CLAMPED_LIGHTNESS_MIN = 32;
const CLAMPED_LIGHTNESS_MAX = 58;
const SATURATION_FLOOR = 55;
const MIN_CONTRAST_ON_WHITE = 4.5;
/** The dark theme's card surface (see the job page's `dark:bg-[#1F1509]`). */
const DARK_SURFACE_HEX = '#1F1509';
/** Warm light ink used when a brand has no usable hue and must still read on the dark surface. */
const LIGHT_INK_ON_DARK_HEX = '#E9DCC3';

export interface AdjustedBrandColor {
  /** Panel fill color, ready to use as a CSS background. */
  panel: string;
  /** Text/icon/stroke color to use on top of `panel`, chosen by measured contrast. */
  on: string;
  /**
   * The brand hue, darkened only as far as needed to read clearly as text on a WHITE
   * background (>=4.5:1 contrast) — for brand-colored text/icons painted on white rather than
   * on `panel`. Do not reuse `on` for this: `on` is chosen for contrast against `panel`, which
   * says nothing about how the raw hue reads against white.
   */
  accentOnWhite: string;
  /** Whether the input hue was usable ('deepened'/'used-as-extracted') or there was none ('neutral-fallback'). */
  mode: 'deepened' | 'used-as-extracted' | 'neutral-fallback';
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((x) =>
        Math.round(Math.min(255, Math.max(0, x)))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
      .toUpperCase()
  );
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h /= 360;
  s /= 100;
  l /= 100;
  let r: number;
  let g: number;
  let b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      let tt = t;
      if (tt < 0) tt += 1;
      if (tt > 1) tt -= 1;
      if (tt < 1 / 6) return p + (q - p) * 6 * tt;
      if (tt < 1 / 2) return q;
      if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return { r: r * 255, g: g * 255, b: b * 255 };
}

function relativeLuminance(r: number, g: number, b: number): number {
  const f = (c: number) => {
    const cs = c / 255;
    return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

function contrastRatio(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2) + 0.05;
  const darker = Math.min(l1, l2) + 0.05;
  return lighter / darker;
}

/**
 * Starting from the panel's own hue/saturation, walks lightness down 1% at a time until the
 * resulting color clears MIN_CONTRAST_ON_WHITE against a white background. A bright/pale brand
 * hue (e.g. lime green) can sit at the panel's clamped 32-58% lightness and still read as under
 * 3:1 against white — nowhere near AA text contrast — so this searches independently of the
 * panel's own lightness rather than assuming the panel color itself is already safe on white.
 */
function darkenForContrastAgainstWhite(h: number, s: number): string {
  for (let l = CLAMPED_LIGHTNESS_MAX; l >= 5; l -= 1) {
    const rgb = hslToRgb(h, s, l);
    const luminance = relativeLuminance(rgb.r, rgb.g, rgb.b);
    if (contrastRatio(luminance, 1) >= MIN_CONTRAST_ON_WHITE) {
      return rgbToHex(rgb.r, rgb.g, rgb.b);
    }
  }
  return DARK_INK_HEX;
}

/** Pure. Returns the neutral fallback for any falsy/malformed input. */
export function adjustBrandColor(hex: string | null | undefined): AdjustedBrandColor {
  if (!hex || !/^#?[0-9a-fA-F]{6}$/.test(hex)) {
    return {
      panel: NEUTRAL_PANEL_FALLBACK_HEX,
      on: WHITE_HEX,
      accentOnWhite: NEUTRAL_PANEL_FALLBACK_HEX,
      mode: 'neutral-fallback',
    };
  }
  const normalized = hex.startsWith('#') ? hex : `#${hex}`;
  const { r, g, b } = hexToRgb(normalized);
  const { h, s, l } = rgbToHsl(r, g, b);

  if (s < MIN_SATURATION_FOR_BRAND_HUE) {
    return {
      panel: NEUTRAL_PANEL_FALLBACK_HEX,
      on: WHITE_HEX,
      accentOnWhite: NEUTRAL_PANEL_FALLBACK_HEX,
      mode: 'neutral-fallback',
    };
  }

  const clampedLightness = Math.min(CLAMPED_LIGHTNESS_MAX, Math.max(CLAMPED_LIGHTNESS_MIN, l));
  const flooredSaturation = Math.max(s, SATURATION_FLOOR);
  const adjustedRgb = hslToRgb(h, flooredSaturation, clampedLightness);
  const panel = rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b);

  const panelLuminance = relativeLuminance(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b);
  const inkRgb = hexToRgb(DARK_INK_HEX);
  const inkLuminance = relativeLuminance(inkRgb.r, inkRgb.g, inkRgb.b);
  const contrastWithWhite = contrastRatio(panelLuminance, 1);
  const contrastWithInk = contrastRatio(panelLuminance, inkLuminance);
  const on = contrastWithWhite >= contrastWithInk ? WHITE_HEX : DARK_INK_HEX;

  const accentOnWhite =
    contrastWithWhite >= MIN_CONTRAST_ON_WHITE ? panel : darkenForContrastAgainstWhite(h, flooredSaturation);

  const changed = Math.abs(clampedLightness - l) > 1 || Math.abs(flooredSaturation - s) > 1;
  return { panel, on, accentOnWhite, mode: changed ? 'deepened' : 'used-as-extracted' };
}

/**
 * Counterpart of `accentOnWhite` for the dark theme: the panel's own hue, lightened only as far
 * as needed to clear 4.5:1 against the dark card surface, so brand-coloured text, strokes and
 * chips stay legible when the page is dark. `panel` itself is a background colour and is
 * routinely near-black (every neutral-fallback brand is), which vanishes as a stroke on the dark
 * surface; this answers that separate question the same way `accentOnWhite` does for white.
 */
export function brandAccentOnDark(panelHex: string | null | undefined): string {
  if (!panelHex || !/^#?[0-9a-fA-F]{6}$/.test(panelHex)) return LIGHT_INK_ON_DARK_HEX;
  const normalized = panelHex.startsWith('#') ? panelHex : `#${panelHex}`;
  const { r, g, b } = hexToRgb(normalized);
  const { h, s, l } = rgbToHsl(r, g, b);
  if (s < MIN_SATURATION_FOR_BRAND_HUE) return LIGHT_INK_ON_DARK_HEX;
  const surface = hexToRgb(DARK_SURFACE_HEX);
  const surfaceLuminance = relativeLuminance(surface.r, surface.g, surface.b);
  for (let lightness = Math.max(l, CLAMPED_LIGHTNESS_MIN); lightness <= 92; lightness += 1) {
    const rgb = hslToRgb(h, s, lightness);
    if (contrastRatio(relativeLuminance(rgb.r, rgb.g, rgb.b), surfaceLuminance) >= MIN_CONTRAST_ON_WHITE) {
      return rgbToHex(rgb.r, rgb.g, rgb.b);
    }
  }
  return LIGHT_INK_ON_DARK_HEX;
}

/**
 * Design system next theme (https://claude.ai/artifact/95wWZCfbxdNRR7p55dAywh, BrandSurface): in the
 * light theme a brand band is never brighter than this WCAG relative luminance, so lime, yellow and
 * neon brands stop shouting while calm brands are untouched. NOT wired into adjustBrandColor yet:
 * today's output is unchanged until the next theme ships.
 */
export const LIGHT_BAND_MAX_LUMINANCE = 0.45;
/** Next theme: the band for employers without a usable colour (soft sun light, graphite dark). */
export const NEXT_NEUTRAL_BAND_LIGHT_HEX = jcThemeHexNextLight.brandFallback.toUpperCase();
export const NEXT_NEUTRAL_BAND_DARK_HEX = jcThemeHexNextDark.brandFallback.toUpperCase();

/**
 * Steps the panel's HSL lightness down 1% at a time (hue and saturation kept) until its relative
 * luminance is at most LIGHT_BAND_MAX_LUMINANCE, then re-picks the text colour by measured contrast.
 * A panel already under the cap comes back as it went in. Pure; malformed input is returned as is.
 */
export function capLightBandLuminance(panelHex: string): { panel: string; on: string } {
  if (!/^#?[0-9a-fA-F]{6}$/.test(panelHex)) return { panel: panelHex, on: DARK_INK_HEX };
  const normalized = panelHex.startsWith('#') ? panelHex : `#${panelHex}`;
  const { r, g, b } = hexToRgb(normalized);
  const { h, s, l } = rgbToHsl(r, g, b);
  let lightness = l;
  let rgb = { r, g, b };
  while (lightness > 0 && relativeLuminance(rgb.r, rgb.g, rgb.b) > LIGHT_BAND_MAX_LUMINANCE) {
    lightness -= 1;
    rgb = hslToRgb(h, s, lightness);
  }
  const panel = lightness === l ? normalized.toUpperCase() : rgbToHex(rgb.r, rgb.g, rgb.b);
  const panelLuminance = relativeLuminance(rgb.r, rgb.g, rgb.b);
  const ink = hexToRgb(DARK_INK_HEX);
  const on =
    contrastRatio(panelLuminance, 1) >= contrastRatio(panelLuminance, relativeLuminance(ink.r, ink.g, ink.b))
      ? WHITE_HEX
      : DARK_INK_HEX;
  return { panel, on };
}

/**
 * Next theme, dark mode: the graphite a colourless near-black band is lifted to: the dim theme's
 * raised neutral, not brandFallback (that is JobCrawls forest now, and a black-branded employer
 * must not turn into our own green).
 */
export const NEXT_DARK_LIFTED_BAND_HEX = jcThemeHexNextDark.elevated.toUpperCase();
/**
 * Next theme, dark mode band brightness window, as WCAG relative luminance (HSL lightness is not
 * perceptual: a fully saturated green at 32% L still glares). Ported from the app's
 * themedBrandPanel (apps/mobile/src/theme/adjust-brand-color.ts), the reference implementation.
 */
const NEXT_DARK_BAND_MIN_LUMINANCE = 0.022;
const NEXT_DARK_BAND_MAX_LUMINANCE = 0.075;
/** Dark mode eases a little saturation off vivid brands so a full-width band reads calm at night. */
const NEXT_DARK_BAND_SATURATION_SCALE = 0.85;

/**
 * Next theme, dark mode: holds a panel inside the brightness window, keeping the hue and easing
 * saturation, so vivid bands stop glaring and near-black ones stop vanishing. A colourless band
 * becomes graphite. Text on it is always white, which clears 4.5:1 across the window. Pure.
 */
export function nextDarkBandPanel(panelHex: string): string {
  if (!/^#?[0-9a-fA-F]{6}$/.test(panelHex)) return panelHex;
  const normalized = panelHex.startsWith('#') ? panelHex : `#${panelHex}`;
  if (normalized.toUpperCase() === NEUTRAL_PANEL_FALLBACK_HEX.toUpperCase()) return NEXT_DARK_LIFTED_BAND_HEX;
  const rgb = hexToRgb(normalized);
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  if (s < MIN_SATURATION_FOR_BRAND_HUE) {
    return relativeLuminance(rgb.r, rgb.g, rgb.b) < NEXT_DARK_BAND_MIN_LUMINANCE
      ? NEXT_DARK_LIFTED_BAND_HEX
      : normalized;
  }
  const sat = s * NEXT_DARK_BAND_SATURATION_SCALE;
  const lumAt = (lightness: number) => {
    const c = hslToRgb(h, sat, lightness);
    return relativeLuminance(c.r, c.g, c.b);
  };
  let lightness = l;
  while (lightness > 1 && lumAt(lightness) > NEXT_DARK_BAND_MAX_LUMINANCE) lightness -= 1;
  while (lightness < 99 && lumAt(lightness) < NEXT_DARK_BAND_MIN_LUMINANCE) lightness += 1;
  const out = hslToRgb(h, sat, lightness);
  return rgbToHex(out.r, out.g, out.b);
}

export type NextThemeBrandColors = {
  light: { panel: string; on: string; accent: string };
  dark: { panel: string; on: string; accent: string };
};

/**
 * The next theme's band colours for a brand in both modes, or null when the brand has no usable
 * colour (the theme's own fallback band tokens cover that). Light: adjustBrandColor, then capped at
 * LIGHT_BAND_MAX_LUMINANCE with the text re-picked. Dark: held in the brightness window with white
 * text. `accent` is the brand colour safe as text on that mode's surface. Pure.
 */
export function nextThemeBrandColors(hex: string | null | undefined): NextThemeBrandColors | null {
  const base = adjustBrandColor(hex);
  if (base.mode === 'neutral-fallback') return null;
  const light = capLightBandLuminance(base.panel);
  const darkPanel = nextDarkBandPanel(base.panel);
  return {
    light: { panel: light.panel, on: light.on, accent: base.accentOnWhite },
    dark: { panel: darkPanel, on: WHITE_HEX, accent: brandAccentOnDark(base.panel) },
  };
}
